import {createContext, useState, useContext} from 'react'
import {createPortal} from 'react-dom'
import {MODAL_STEPS} from '@/constants/steps'

export const BookingModalContext = createContext(null)

export function BookingModalShell({children, step, overlay = false}) {
    const [headerEl, setHeaderEl] = useState(null)
    const [footerEl, setFooterEl] = useState(null)

    const innerClass = 'flex-1 overflow-y-auto overflow-x-hidden min-h-0 hopn-modal-container-inner'
    // Booking-received is the one step that runs edge to edge: its shell is transparent
    // (the cards are the surfaces) and the mobile CSS gives the container height: 100dvh,
    // so capping it here would leave the deck short of the viewport. It is therefore
    // excluded from the phone rule below as well.
    //
    // Every other step is capped HERE, not by the container's own `max-height`:
    // .hopn-modal-container allows 98dvh on mobile, but this shell sits inside it, so the
    // smaller number always won.
    //
    // ── Two caps, and the PHONE is the taller one ──────────────────────────────────
    // Desktop 90dvh: a centred dialog wants air around it, and at 95dvh it crowded the
    // top and bottom of the window.
    // Phone 95dvh: the sheet is not a dialog on a page, it IS the page, so it should take
    // the screen — at 90dvh the footer button sat clear of the bottom with a band of the
    // page showing under it, and the sheet read as visibly shorter than the plugin's on
    // the same step (selectedCarDetailsLatest, where the footer is the primary action).
    const maxHClass = step === MODAL_STEPS.BOOKING_RECEIVED
        ? 'max-h-[100dvh]'
        : 'max-md:max-h-[95dvh] max-h-[90dvh]'

    return (
        // `overlay` marks a stacked modal (mobile nested popup) so ModalHeader renders a close (✕) instead of back.
        <BookingModalContext.Provider value={{headerEl, footerEl, overlay}}>
            <div className={`flex flex-col ${maxHClass} overflow-hidden`}>
                {/* `hopn-modal-shell-header` (same name as the plugin's shell) is the CSS
                    hook for "the STEP's own header": a non-inline ModalHeader portals in
                    here, while a nested .upper-modal's inline header renders in place. Any
                    per-step header rule must go through it or it also hits those popups. */}
                <div ref={setHeaderEl} className="shrink-0 hopn-modal-shell-header"/>
                <div className={innerClass}>
                    {children}
                </div>
                <div ref={setFooterEl} className="shrink-0"/>
            </div>
        </BookingModalContext.Provider>
    )
}

/**
 * Portal-based footer slot. Replaces `<div className="hopn-modal-footer">…</div>`
 * inside booking modalPages — when rendered within a BookingModalShell, it
 * portals into the shell's footer slot so it sits outside the scroll area.
 * Falls back to inline rendering if no shell is mounted.
 */
export function ModalFooter({className = '', children, ...rest}) {
    const ctx = useContext(BookingModalContext)
    const node = (
        <div className={`hopn-modal-footer ${className}`.trim()} {...rest}>
            {children}
        </div>
    )
    if (ctx?.footerEl) return createPortal(node, ctx.footerEl)
    return node
}

export default BookingModalShell
